import { useMemo } from "react";
import { Clock } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type TimePickerProps = {
  id?: string;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  minuteStep?: number;
  placeholder?: string;
  className?: string;
};

const formatTimeLabel = (value: string) => {
  const [hourText, minute = "00"] = value.split(":");
  const hour = Number(hourText);
  const hour12 = ((hour + 11) % 12) + 1;
  return `${hour12.toString().padStart(2, "0")}:${minute} ${hour < 12 ? "AM" : "PM"}`;
};

export function TimePicker({
  id,
  value = "",
  onChange,
  disabled,
  minuteStep = 15,
  placeholder = "Select time",
  className,
}: TimePickerProps) {
  const options = useMemo(() => {
    const safeStep = minuteStep > 0 && minuteStep <= 60 ? minuteStep : 15;
    return Array.from({ length: Math.ceil((24 * 60) / safeStep) }, (_, index) => {
      const totalMinutes = index * safeStep;
      const hour = Math.floor(totalMinutes / 60);
      const minute = totalMinutes % 60;
      const optionValue = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      return { value: optionValue, label: formatTimeLabel(optionValue) };
    });
  }, [minuteStep]);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className={cn("w-full", className)}>
        <Clock className="size-4 text-muted-foreground" aria-hidden="true" />
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {value && !options.some((option) => option.value === value) && (
          <SelectItem value={value}>{formatTimeLabel(value)}</SelectItem>
        )}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
